import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/atoms/UI/Icon/Icon';
import Button from '@/components/atoms/UI/buttons/Button';
import Grid from '@/components/molecules/Grid';
import { ModalTitle, PopupInnerVerticalStretch } from '@/components/atoms/UI/Modal/styles';
import WidgetPricingItem from '@/components/features/widgets/WidgetPricingItem/WidgetPricingItem';
import { WidgetPricingProps } from '@/store/types/widgetPricingTypes';
import { ROUTES } from '@/constants/urls';

type Props = {
	isPopup?: boolean;
	onEmbedClick: () => void;
};

const WidgetPricing: React.FC<Props> = ({isPopup = true, onEmbedClick}) => {
	const { t } = useTranslation();
	const text_title = t('widget.tariff');
	const text_find_more = t('widget.find_more');
	const text_embed = t('embedTest');
	const text_pay = t('pay');
	const text_contact_us = t('contact_us');
	const text_price_premium = t('widget.price_premium');
	const text_price_agreement = t('widget.price_agreement');
	const text_individual_plan = t('widget.individual');
	const text_free_price = `0 ${t('widget.currency')}`;
	const text_free_plan = t('widget.free');
	const text_premium_plan = t('widget.premium');
	const text_unlimited_tests = t('widget.unlimited_tests');
	const text_without_adv = t('widget.without_adv');
	const text_price_premium_limit = t('widget.price_premium_limit');
	const text_show_adv = t('widget.show_adv');
	const text_add_link_in_result = t('widget.add_link_in_result');
	const text_change_blocks_in_result = t('widget.change_blocks_in_result');
	const text_get_lids = t('widget.get_lids');
	const text_integration_amocrm = t('widget.integration_amocrm');

	const [selectedPlan, setSelectedPlan] = useState<string | null>(text_free_plan);

	const pricingPlans: WidgetPricingProps[] = [
		{
			planType: text_free_plan,
			price: text_free_price,
			features: [
				{ name: text_unlimited_tests },
				{ name: text_show_adv, special: true },
			],
			demoUrl: ROUTES.WIDGETS_DEMO_FREE,
			selected: selectedPlan === text_free_plan,
			isPopup: isPopup,
		},
		{
			planType: text_premium_plan,
			price: text_price_premium,
			features: [
				{ name: text_unlimited_tests },
				{ name: text_without_adv },
				{ name: text_price_premium_limit, special: true },
			],
			demoUrl: ROUTES.WIDGETS_DEMO_PREMIUM,
			selected: selectedPlan === text_premium_plan,
			isPopup: isPopup,
		},
		{
			planType: text_individual_plan,
			price: text_price_agreement,
			features: [
				{ name: text_unlimited_tests },
				{ name: text_without_adv },
				{ name: text_add_link_in_result },
				{ name: text_change_blocks_in_result },
				{ name: text_get_lids },
				{ name: text_integration_amocrm },
			],
			demoUrl: ROUTES.WIDGETS_DEMO_INDIVIDUAL,
			selected: selectedPlan === text_individual_plan,
			isPopup: isPopup,
		},
	];

	const handleSelectPlan = (planType: string) => {
		setSelectedPlan(planType);
	};

	return (
		<PopupInnerVerticalStretch>
			<Grid col='1' gap='3' centeredContent>
				<ModalTitle>{text_title}</ModalTitle>
				<Grid col={'1'} gap={'2'}>
					{pricingPlans.map((plan, index) => (
						<WidgetPricingItem
							key={index}
							{...plan}
							onClick={() => handleSelectPlan(plan.planType)}
						/>
					))}
				</Grid>
			</Grid>

			<Grid col='1' gap='2' centeredContent>
				<Button link autoWidth>{text_find_more}<Icon name={'Next'} small /></Button>
				{(selectedPlan === text_free_plan) && <Button onClick={onEmbedClick}>{text_embed}</Button>}
				{(selectedPlan === text_premium_plan) && <Button href={ROUTES.PAY_WIDGET}>{text_pay}</Button>}
				{(selectedPlan === text_individual_plan) && <Button href={ROUTES.CONTACTS}>{text_contact_us}</Button>}
			</Grid>
		</PopupInnerVerticalStretch>
	);
};

export default React.memo(WidgetPricing);
